import React, {
    ReactNode, useCallback, useEffect, useMemo, useRef,
} from "react";
import calculateStats from "../calculateStats";

interface Props {
    summary: Summary;
    open: boolean;
    onClose: () => void;
    onFilter: (filter: FilterOptions) => void;
}

export default function Stats({
    summary, open, onClose, onFilter,
}: Props): JSX.Element {
    const ref = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        if (open) {
            ref.current?.showModal();
        } else {
            ref.current?.close();
        }
    }, [open]);

    const stats = useMemo(() => calculateStats(summary), [summary]);

    return (
        <dialog
            ref={ref}
            className={"m-0 h-screen w-screen max-h-none max-w-none bg-blue-zodiac-900/95 p-4 "
                + `text-white ${open ? "" : "hidden "}backdrop:bg-blue-zodiac-400/60`}
            role="presentation"
            onCancel={onClose}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            tabIndex={-1}
            onKeyUp={(e) => { if (e.key === "Escape") onClose(); }}
        >
            <div className="flex justify-end">
                <button
                    type="button"
                    className="h-12 w-12 rounded-full bg-white text-center text-xl text-gray-800 hover:bg-gray-300"
                    onClick={onClose}
                >
                    ✕
                </button>
            </div>
            <div className="my-2">
                <table className="w-full">
                    <thead>
                        <tr>
                            <th colSpan={3}>
                                <h2 className="text-center text-xl font-medium uppercase">
                                    Games
                                </h2>
                            </th>
                            <th aria-label="spacer" />
                            <th colSpan={6}>
                                <h2 className="text-center text-xl font-medium uppercase">
                                    Serving
                                </h2>
                            </th>
                        </tr>
                        <tr>
                            <th className="sr-only">Name</th>
                            <th className="p-1 font-normal">Played</th>
                            <th className="p-1 font-normal">Won (%)</th>

                            <XSpace />

                            <th className="p-1 font-normal">Games</th>
                            <th className="p-1 font-normal">Holds (%)</th>
                            <th className="p-1 font-normal">Break (%)</th>
                            <th className="p-1 font-normal">Serves</th>
                            <th className="p-1 font-normal">First Faults (%)</th>
                            <th className="p-1 font-normal">Second Faults (%)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <TeamStatsRows
                            teamStats={stats.teamStats[0]}
                            playerStats={[
                                stats.playerStats[summary.players[0][0]],
                                stats.playerStats[summary.players[0][1]],
                            ]}
                        />
                        <tr>
                            <YSpace />
                        </tr>
                        <TeamStatsRows
                            teamStats={stats.teamStats[1]}
                            playerStats={[
                                stats.playerStats[summary.players[1][0]],
                                stats.playerStats[summary.players[1][1]],
                            ]}
                        />
                    </tbody>
                </table>
            </div>
            <div className="my-2">
                <table className="w-full">
                    <thead>
                        <tr>
                            <th colSpan={11}>
                                <h2 className="sr-only">
                                    Wins / Losses
                                </h2>
                            </th>
                            <th colSpan={7}>
                                <h2 className="text-center text-xl font-medium uppercase">
                                    Overall
                                </h2>
                            </th>
                        </tr>
                        <tr>
                            <th className="sr-only">
                                Name
                            </th>
                            <th colSpan={2} className="p-1 font-normal">Forehand</th>
                            <th colSpan={2} className="p-1 font-normal">Backhand</th>
                            <th colSpan={2} className="p-1 font-normal">Volleys</th>
                            <th colSpan={2} className="p-1 font-normal">Screen / Lobs</th>
                            <th colSpan={2} className="p-1 font-normal">Overhead</th>

                            <th rowSpan={2} className="p-1 font-normal">Winners</th>
                            <th rowSpan={2} className="p-1 font-normal">Winners / Game</th>
                            <th rowSpan={2} className="p-1 font-normal">Losers</th>
                            <th rowSpan={2} className="p-1 font-normal">Losers / Game</th>
                        </tr>
                        <tr>
                            <th className="sr-only">Name</th>
                            <th className="p-1 font-normal">W</th>
                            <th className="p-1 font-normal">L</th>
                            <th className="p-1 font-normal">W</th>
                            <th className="p-1 font-normal">L</th>
                            <th className="p-1 font-normal">W</th>
                            <th className="p-1 font-normal">L</th>
                            <th className="p-1 font-normal">W</th>
                            <th className="p-1 font-normal">L</th>
                            <th className="p-1 font-normal">W</th>
                            <th className="p-1 font-normal">L</th>
                        </tr>
                    </thead>
                    <tbody>
                        <PlayerRow
                            games={stats.games}
                            stats={stats.playerStats[summary.players[0][0]]}
                            onFilter={onFilter}
                        />
                        <PlayerRow
                            games={stats.games}
                            stats={stats.playerStats[summary.players[0][1]]}
                            onFilter={onFilter}
                        />
                        <PlayerRow
                            games={stats.games}
                            stats={stats.playerStats[summary.players[1][0]]}
                            onFilter={onFilter}
                        />
                        <PlayerRow
                            games={stats.games}
                            stats={stats.playerStats[summary.players[1][1]]}
                            onFilter={onFilter}
                        />
                    </tbody>
                </table>
            </div>
        </dialog>
    );
}

function NameData({ children }: { children: ReactNode }): JSX.Element {
    return (
        <td className="bg-blue-zodiac-950 p-2 text-left text-lg font-medium">
            {children}
        </td>
    );
}

function FilterData(
    {
        player,
        outcome,
        shot,
        children,
        onClick,
    }: {
        player: string,
        outcome: "W" | "L",
        shot: string,
        children: ReactNode,
        onClick: (filter: FilterOptions) => void,
    },
): JSX.Element {
    const onClickButton = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        onClick({
            player: [player],
            outcome: [outcome],
            shot: [shot],
        });
    }, [onClick, player, outcome, shot]);

    return (
        <td className="bg-blue-zodiac-950 p-4 text-center">
            <button type="button" className="rounded bg-blue-zodiac-600 px-4 py-2" onClick={onClickButton}>
                {children}
            </button>
        </td>
    );
}

function PillData({ rowSpan, children }: { rowSpan?: number, children: ReactNode }): JSX.Element {
    return (
        <td className="bg-blue-zodiac-950 p-2 text-center" rowSpan={rowSpan}>
            <div className="rounded bg-blue-zodiac-800 p-1">
                {children}
            </div>
        </td>
    );
}
PillData.defaultProps = { rowSpan: undefined };

function XSpace(): JSX.Element {
    return <td aria-label="spacer" className="w-4" />;
}

function YSpace(): JSX.Element {
    return <td aria-label="spacer" className="h-4" />;
}

function percentOr(divisor: number, dividend: number, or = "–") {
    if (divisor === 0) {
        return or;
    }

    return `${dividend} (${Math.round((dividend / divisor) * 100)}%)`;
}

function numberOr(number: number) {
    return number || " ";
}

interface TeamStatsRowsProps {
    teamStats: TeamStats;
    playerStats: [PlayerStats, PlayerStats];
}

function TeamStatsRows({
    teamStats: { played, won },
    playerStats,
}: TeamStatsRowsProps): JSX.Element {
    const a = playerStats[0];
    const b = playerStats[1];

    return (
        <>
            <tr>
                <NameData>{a.name}</NameData>
                <PillData rowSpan={2}>{played}</PillData>
                <PillData rowSpan={2}>{`${won} (${Math.round((won / played) * 100) || 0}%)`}</PillData>

                <XSpace />

                <PillData>{a.gamesServed}</PillData>
                <PillData>
                    {percentOr(a.gamesServed, a.holds)}
                </PillData>
                <PillData>
                    {percentOr(a.gamesServed, a.breaks)}
                </PillData>
                <PillData>{a.serves}</PillData>
                <PillData>
                    {percentOr(a.serves, a.firstFaults)}
                </PillData>
                <PillData>
                    {percentOr(a.serves, a.secondFaults)}
                </PillData>
            </tr>
            <tr>
                <NameData>{b.name}</NameData>

                <XSpace />

                <PillData>{b.gamesServed}</PillData>
                <PillData>
                    {percentOr(b.gamesServed, b.holds)}
                </PillData>
                <PillData>
                    {percentOr(b.gamesServed, b.breaks)}
                </PillData>
                <PillData>{b.serves}</PillData>
                <PillData>
                    {percentOr(b.serves, b.firstFaults)}
                </PillData>
                <PillData>
                    {percentOr(b.serves, b.secondFaults)}
                </PillData>
            </tr>
        </>
    );
}

interface PlayerRowProps {
    games: Game[];
    stats: PlayerStats;
    onFilter: (filter: FilterOptions) => void;
}

function PlayerRow({ games, stats, onFilter }: PlayerRowProps): JSX.Element {
    const totalGames = games.length;
    return (
        <tr>
            <NameData>{stats.name}</NameData>

            <FilterData
                player={stats.name}
                outcome="W"
                shot="Forehand"
                onClick={onFilter}
            >
                {numberOr(stats.forehand.win)}
            </FilterData>
            <FilterData
                player={stats.name}
                outcome="L"
                shot="Forehand"
                onClick={onFilter}
            >
                {numberOr(stats.forehand.lose)}
            </FilterData>
            <FilterData
                player={stats.name}
                outcome="W"
                shot="Backhand"
                onClick={onFilter}
            >
                {numberOr(stats.backhand.win)}
            </FilterData>
            <FilterData
                player={stats.name}
                outcome="L"
                shot="Backhand"
                onClick={onFilter}
            >
                {numberOr(stats.backhand.lose)}
            </FilterData>
            <FilterData
                player={stats.name}
                outcome="W"
                shot="Volley"
                onClick={onFilter}
            >
                {numberOr(stats.volley.win)}
            </FilterData>
            <FilterData
                player={stats.name}
                outcome="L"
                shot="Volley"
                onClick={onFilter}
            >
                {numberOr(stats.volley.lose)}
            </FilterData>
            <FilterData
                player={stats.name}
                outcome="W"
                shot="Screen/Lob"
                onClick={onFilter}
            >
                {numberOr(stats.screenLob.win)}
            </FilterData>
            <FilterData
                player={stats.name}
                outcome="L"
                shot="Lob"
                onClick={onFilter}
            >
                {numberOr(stats.screenLob.lose)}
            </FilterData>
            <FilterData
                player={stats.name}
                outcome="W"
                shot="Overhead"
                onClick={onFilter}
            >
                {numberOr(stats.overhead.win)}
            </FilterData>
            <FilterData
                player={stats.name}
                outcome="L"
                shot="Overhead"
                onClick={onFilter}
            >
                {numberOr(stats.overhead.lose)}
            </FilterData>

            <PillData>{stats.winners}</PillData>
            <PillData>{(stats.winners / totalGames || 0).toFixed(1)}</PillData>
            <PillData>{stats.losers}</PillData>
            <PillData>{(stats.losers / totalGames || 0).toFixed(1)}</PillData>
        </tr>
    );
}
